import "./public-path";

import React from "react";
import ReactJSXRuntime from "react/jsx-runtime";
import ReactJSXDevRuntime from "react/jsx-dev-runtime";
import ReactDOM from "react-dom";
import ReactDOMServer from "react-dom/server";
import ReactDOMClient from "react-dom/client";
import * as MuiMaterial from "@mui/material";
import EmotionStyled from "@emotion/styled";
import * as EmotionReact from "@emotion/react";
import { initTranslator } from "@kaltura/ds-react-utils";
import { translate, translatePlural } from "@kaltura/mediaspace-shared-utils";

/**
 * global error handler, final boundary to avoid js errors crashing the page
 * @param error
 */
window.onerror = function (error) {
    console.error("received a fatal Javascript error: ", { error: error });
};

// Init translation utils into DS components lib
initTranslator(translate, translatePlural);

export { React, ReactJSXRuntime, ReactJSXDevRuntime };
export { ReactDOM };
export { ReactDOMServer };
export { ReactDOMClient };
export { MuiMaterial, EmotionStyled, EmotionReact };
export * as ReactHookForm from "react-hook-form";

export * as DsLayout from "@kaltura/mediaspace-shared-layout";
export * as Contexts from "@kaltura/mediaspace-shared-contexts";
export * as Config from "@kaltura/mediaspace-shared-data-kms-config";
export * as HomePage from "./features/home-page/App";
export * as GroupPage from "./features/group-page/App";
export * as ListPage from "./features/list-page/App";
export * as Registration from "./features/registration/App";
export * as Login from "./features/login/App";
export * as Agenda from "./features/agenda/App";
export * as HeaderMenu from "./shared/header-menu/App";
export * as Leaderboard from "./features/leaderboard/App";
export * as PersonalProfile from "./features/personal-profile/App";
export * as Footer from "./shared/footer/App";
export * as SimpleSiteHeader from "./shared/simple-site-header/App";
export * as HelpPage from "./features/help-page/App";
export * as Kcapture from "./features/kcapture/App";
export * as Recorder from "./shared/recorder/App";
export * as UploadMedia from "./features/upload-media/App";
export * as Studio from "./features/studio/App";
export * as PreviewBanner from "./features/preview-banner/App";
export * as ChannelsCollections from "./features/channels-collections/App";
export * as CategoryPage from "./features/category-page/App";
export * as CategoryEdit from "./features/category-edit/App";
export * as Channels from "./features/channels/App";
export * as EditBoardContainer from "./features/board-messages/App";
export * as EmailLogin from "./features/email-login/App";
export * as PrivacyBanner from "./features/privacy-banner/App";
export * as RegistrationEditor from "./features/registration-editor/App";
export * as InviteUsers from "./features/invite-users/App";
export * as AuthBrokerProfilesPage from "./features/auth-broker-profiles-page/App";
export * as RestreamDestinations from "./features/restream-destinations/App";
export * as Certificates from "./features/certificates/App";
export * as PlayerUpgradeTool from "./features/players-upgrade/App";
export * as BulkLoginLinks from "./features/bulk-login-links/App";
export * as Cnc from "./features/cnc/App";
export * as ChannelTV from "./features/channeltv/App";
export * as EditCustomLocale from "./features/edit-custom-locale/App";
export * as Disclaimer from "./features/disclaimer/App";
export * as AuthBrokerLogoutPage from "./features/auth-broker-logout-page/App";
export * as MediaPage from "./features/media-page/App";
export * as ErrorPage from "./features/error-page/App";
export * as GlobalSearch from "./features/global-search/App";
export * as LegacyPort from "./shared/legacy-port/App";
export * as InspireMe from "./features/inspire-me/App";
export * as PlaylistsGrid from "./features/playlists-grid/App";
export * as WaitingRoom from "./features/waiting-room/App";
export * as CheckIn from "./features/event-check-in/App";
export * as MyMedia from "./features/my-media/App";
export * as BSE from "./features/bse/App";
export * as MediaActions from "./shared/media-actions/App";
export * as ManageSubsciption from "./shared/manage-messaging-subsciption/App";
export * as UI from "./shared/ui/App";
export * as Player from "./shared/player/App";
export * as Utils from "./shared/utils";
export * as AddNewIllustrations from "./features/add-new/App";
export * as CategoryModeration from "./features/category-moderation/App";
export * as MeetingRoom from "./shared/meeting-entry/App";
export * as AddToCategory from "./features/add-to-category/App";
export * as EditEntry from "./features/edit-entry/App";
export * as CreateChannelModalContainer from "./features/create-channel-modal-container/App";
export * as AddQuiz from "./features/add-quiz/App";
export * as LiveStudio from "./features/live-studio/App";
export { YouTubeImport } from "@kaltura/mediaspace-shared-youtube-import";
export { CreateCategoryModal } from "@kaltura/mediaspace-shared-create-category-modal";
export { SimpleMediaSearch } from "@kaltura/mediaspace-shared-search";
export * as Folders from "./shared/folders/App";
export { CreateTownhallModal } from "@kaltura/mediaspace-features-create-townhall-modal";
export { CreateRoomModal } from "@kaltura/mediaspace-features-create-room-modal";
export { CreateDraftEntryModal } from "@kaltura/mediaspace-features-create-draft-entry-modal";
export { CreatePlaylistModal } from "@kaltura/mediaspace-shared-create-playlist-modal";
export * as MyPlaylists from "./features/my-playlists/App";
export * as PlaylistDetails from "./features/playlist-details/App";
export { LinkMenuItem, ActionMenuItem } from "@kaltura/mediaspace-shared-ui";
export { Share } from "@kaltura/mediaspace-shared-share";
export * as InviteToMedia from "./features/invite-to-media/App";
export * as Icons from "./shared/icons/App";
export * as EditPlaylist from "./shared/edit-playlist/App";
export * as Genie from "./features/genie/App";
export * as Editor from "./features/editor/App";
export { AddFromUrl } from "@kaltura/mediaspace-features-add-from-url";
export * as Channel from "./shared/channel/App";
export * as MyHistory from "./features/my-history/App";
export * as UserReports from "./features/user-reports/App";
export * as VodAvatars from "./features/vod-avatars/App";
export * as HeaderLogoOnly from "./shared/header-logo-only/App";
export * as CaptionsEditor from "./features/captions-editor/App";
export * as Hostnames from "./features/hostnames/App";
export * as RecycleBin from "./features/recycle-bin/App";
export * as Moe from "./features/moe/App";
export * as Feedback from "./features/feedback/App";
export * as Restream from "./features/restream/App";
export * as PWA from "@kaltura/mediaspace-features-pwa";
